// ============================================================================
// ROOM SERVICE
// ============================================================================
// Business logic and API calls for Room module

import { get, post, put, del } from '../client/axiosClient';
import { roomEndpoints } from '../endpoints/room.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Room,
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomSearchRequest,
} from '../types';

/**
 * Room Service Class
 */
class RoomService {
  /**
   * Get all rooms (paginated)
   */
  async getAll(params?: RoomSearchRequest): Promise<PaginatedResponse<Room>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Room', params)
      : roomEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get room by ID
   */
  async getById(id: ID): Promise<ApiResponse<Room>> {
    const response = await get<ApiResponse<Room>>(roomEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get room by room number
   */
  async getByNumber(roomNumber: string, propertyId?: string): Promise<ApiResponse<Room>> {
    let endpoint = roomEndpoints.getByNumber(roomNumber);
    if (propertyId) {
      endpoint = `${endpoint}?propertyId=${propertyId}`;
    }
    const response = await get<ApiResponse<Room>>(endpoint);
    return response.data;
  }

  /**
   * Create new room
   */
  async create(data: CreateRoomRequest): Promise<ApiResponse<Room>> {
    const response = await post<ApiResponse<Room>>(
      roomEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update room
   */
  async update(id: ID, data: UpdateRoomRequest): Promise<ApiResponse<Room>> {
    const response = await put<ApiResponse<Room>>(
      roomEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete room
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(roomEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate room number uniqueness
   */
  async validateNumber(roomNumber: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = roomEndpoints.validateNumber(roomNumber);
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) {
      endpoint = `${endpoint}?${queryParts.join('&')}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const roomService = new RoomService();
export default roomService;
